import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Download, ImageIcon, Sparkles, Upload, X } from 'lucide-react'
import { api } from '@/services/api'
import { categoryOf } from '@/services/modelCategory'
import {
  IMAGE_EDITING,
  IMAGE_TO_VIDEO,
  type GeneratedImage,
  type LocalModel,
} from '@/services/types'

/**
 * Making pictures with an open model.
 *
 * <p><strong>Where the drawing happens is not what this page used to say.</strong>
 * It claimed the model runs on the reader's own machine and that nothing is
 * sent anywhere. Neither is true as built: {@code /generate} calls the
 * server's own diffusion engine, and the desktop app talks to the hosted API
 * rather than a local one, so the prompt leaves the machine in both cases. The
 * claim was written for the device path that downloads already use and
 * generation does not.
 *
 * <p>It now says so again, and this time it is true: generation is dispatched
 * to the paired device the way {@code /pull} already was, so with a device
 * connected the prompt and the picture stay on it. Without one the work falls
 * back to the server, and the page says that instead. The page asks rather
 * than assuming, because both are real states.
 *
 * <p>A page of its own rather than a mode of the composer. Generating needs
 * controls a message box has nowhere to put -- a shape, a count, and a model
 * that is not the one answering questions -- and the result is a grid to look
 * at and keep rather than a turn in a conversation.
 *
 * <p>The wait is the thing this page is mostly designed around. Without a
 * graphics card an image is minutes, which is long enough that a spinner alone
 * reads as broken, so the elapsed time is shown and the estimate is stated
 * before anyone commits to it.
 */

/** Shapes people actually ask for, rather than two number boxes. */
/**
 * Clip lengths, as durations rather than frame counts.
 *
 * The engine counts frames and nobody thinks in frames. Sixteen a second is
 * what these models are trained at, so the arithmetic is done here once
 * instead of asking the reader to do it every time.
 */
const LENGTHS = [
  { id: 'short', label: '2 seconds', frames: 33 },
  { id: 'medium', label: '3 seconds', frames: 49 },
  { id: 'long', label: '5 seconds', frames: 81 },
] as const

const SHAPES = [
  { id: 'square', label: 'Square', width: 1024, height: 1024 },
  { id: 'portrait', label: 'Portrait', width: 768, height: 1024 },
  { id: 'landscape', label: 'Landscape', width: 1024, height: 768 },
] as const

type ShapeId = (typeof SHAPES)[number]['id']
type LengthId = (typeof LENGTHS)[number]['id']

export function ImagesPage() {
  const [models, setModels] = useState<LocalModel[]>([])
  const [loading, setLoading] = useState(true)
  const [modelId, setModelId] = useState('')
  const [prompt, setPrompt] = useState('')
  const [shape, setShape] = useState<ShapeId>('square')
  const [length, setLength] = useState<LengthId>('short')
  const [count, setCount] = useState(1)
  const [source, setSource] = useState<File | null>(null)
  const [images, setImages] = useState<GeneratedImage[]>([])
  const [busy, setBusy] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Where the work will happen. Worth saying, and only honest if asked.
  const [onDevice, setOnDevice] = useState<boolean | null>(null)

  const load = useCallback(async () => {
    try {
      const all = await api.listLocalModels()
      // Installed only. An image model that is merely in the catalogue cannot
      // answer, and offering it here would mean discovering that after typing
      // a prompt rather than before.
      // Both, because the page makes both. A clip is the same request with a
      // mode on it, and a model that only films would otherwise be installed
      // and then unreachable.
      setModels(
        all.filter(
          (m) =>
            m.installed &&
            (categoryOf(m) === 'IMAGE_GENERATION' || categoryOf(m) === 'VIDEO_GENERATION'),
        ),
      )
      const status = await api.modelRuntimeStatus().catch(() => null)
      setOnDevice(status?.agentConnected ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your models.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // Pick one as soon as there is one to pick, and let go of a model that has
  // been removed from another tab.
  useEffect(() => {
    if (models.length === 0) {
      setModelId('')
    } else if (!models.some((m) => m.id === modelId)) {
      setModelId(models[0].id)
    }
  }, [models, modelId])

  // Counts up while the engine works. Cleared on every state change rather
  // than only on success, so a failure does not leave a clock running.
  const startedAt = useRef(0)
  useEffect(() => {
    if (!busy) return
    startedAt.current = Date.now()
    setElapsed(0)
    const tick = setInterval(
      () => setElapsed(Math.round((Date.now() - startedAt.current) / 1000)),
      1000,
    )
    return () => clearInterval(tick)
  }, [busy])

  const selected = useMemo(
    () => models.find((m) => m.id === modelId) ?? null,
    [models, modelId],
  )

  /**
   * Whether the chosen model takes a picture as a starting point at all --
   * to change it, or to animate it. Both arrive the same way from here.
   */
  const canEdit = Boolean(
    selected?.capabilities.includes(IMAGE_EDITING) ||
      selected?.capabilities.includes(IMAGE_TO_VIDEO),
  )

  /**
   * A clip rather than a still, which changes what the button offers to do.
   *
   * <p>Guarded on there being a model at all. There is a render between the
   * list arriving and one being chosen -- the effect that chooses runs after
   * it -- and casting an empty object to a model to get through that window
   * took the whole page down, because a model with no capabilities is not a
   * model this code can read.
   */
  const makesVideo = selected !== null && categoryOf(selected) === 'VIDEO_GENERATION'

  // A model that cannot edit must not be handed a picture, or the request
  // would be refused by the server after the upload rather than before it.
  useEffect(() => {
    if (!canEdit) setSource(null)
  }, [canEdit])

  // Object URLs are a leak until revoked, and this one is replaced every time
  // a different picture is chosen.
  const [preview, setPreview] = useState<string | null>(null)
  useEffect(() => {
    if (!source) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(source)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [source])

  const generate = async () => {
    if (!modelId || !prompt.trim() || busy) return
    setBusy(true)
    setError(null)
    // Cleared up front: keeping the previous batch on screen under a spinner
    // makes it look like the new one arrived.
    setImages([])
    const { width, height } = SHAPES.find((s) => s.id === shape) ?? SHAPES[0]
    const frames = LENGTHS.find((l) => l.id === length)?.frames
    // Sent only where it means something. A model that makes stills would
    // ignore it, and a request carrying settings for something it is not
    // doing is a request that is harder to read when it goes wrong.
    const asked = {
      prompt: prompt.trim(),
      width,
      height,
      n: count,
      ...(makesVideo ? { frames } : {}),
    }
    try {
      // Editing when a picture was chosen, making one when it was not. The
      // same button either way: what the model is being asked for is visible
      // on the page, and a second button would only ever be wrong for one of
      // the two states.
      const result = source
        ? await api.editImage(modelId, asked, source)
        : await api.generateImage(modelId, asked)
      // Defended rather than trusted. `request` parses JSON and casts; it
      // checks nothing at run time, so a proxy or a future server answering
      // 200 with a body of another shape lands here as undefined -- and
      // `images.length` in the render below then takes the whole page down to
      // a white screen, which is a worse failure than the one that caused it.
      setImages(result?.images ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The image could not be made.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto h-full w-full max-w-4xl overflow-y-auto px-6 py-8">
      <h1 className="mb-1 text-xl font-extrabold tracking-display">Images</h1>
      <p className="mb-5 text-sm text-content-muted">
        Describe a picture or a short clip and an open model will make it. Without a
        graphics card this takes a few minutes.{' '}
        {onDevice === true && (
          <>Drawing happens on your paired device, and nothing leaves it.</>
        )}
        {onDevice === false && (
          <>
            Drawing happens on NevTan&rsquo;s server. To keep it on your own machine,
            pair a device from{' '}
            <Link to="/settings" className="underline hover:text-content">
              Settings
            </Link>
            .
          </>
        )}
      </p>

      {error && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm text-danger">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : models.length === 0 ? (
        <NoModels />
      ) : (
        <>
          <div className="rounded-xl border border-border bg-surface-raised p-4">
            <label htmlFor="image-prompt" className="mb-1.5 block text-sm font-semibold">
              {source
                ? makesVideo
                  ? 'How should it move?'
                  : 'What should it change?'
                : makesVideo
                  ? 'What should the clip show?'
                  : 'What should it draw?'}
            </label>

            {/* Only where the model can act on it. Offering an upload that
                the server will refuse wastes the upload and explains nothing. */}
            {canEdit && (
              <SourcePicture
                file={source}
                preview={preview}
                onChange={setSource}
                animates={makesVideo}
              />
            )}
            <textarea
              id="image-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                // The button is a long way from the keyboard once the textarea
                // has grown, and plain Enter has to stay a newline.
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void generate()
              }}
              rows={3}
              placeholder={
                source
                  ? 'Make the sky orange, and the sign say OPEN'
                  : 'A lighthouse in a storm, painted in thick oils'
              }
              className="w-full resize-y rounded-lg bg-surface-sunken px-3 py-2 text-sm outline-none placeholder:text-content-muted"
            />

            <div className="mt-3 flex flex-wrap items-end gap-4">
              <Field label="Shape">
                <div className="flex gap-1">
                  {SHAPES.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setShape(option.id)}
                      aria-pressed={shape === option.id}
                      className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                        shape === option.id
                          ? 'bg-accent text-accent-content'
                          : 'bg-surface-sunken text-content-muted hover:text-content'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </Field>

              {/* A clip has a length; a still does not. */}
              {makesVideo && (
                <Field label="Length">
                  <div className="flex gap-1">
                    {LENGTHS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setLength(option.id)}
                        aria-pressed={length === option.id}
                        className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                          length === option.id
                            ? 'bg-accent text-accent-content'
                            : 'bg-surface-sunken text-content-muted hover:text-content'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </Field>
              )}

              <Field label="How many">
                <select
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value))}
                  aria-label="How many images"
                  className="rounded-lg bg-surface-sunken px-2.5 py-1.5 text-xs font-semibold outline-none"
                >
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Only worth a control when there is a choice to make. */}
              {models.length > 1 && (
                <Field label="Model">
                  <select
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                    aria-label="Model"
                    className="rounded-lg bg-surface-sunken px-2.5 py-1.5 text-xs font-semibold outline-none"
                  >
                    {models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <button
                type="button"
                onClick={() => void generate()}
                disabled={busy || !prompt.trim()}
                className="ml-auto flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-content shadow-raised transition hover:opacity-90 disabled:opacity-50"
              >
                <Sparkles size={15} />
                {actionLabel(makesVideo, source !== null, busy)}
              </button>
            </div>

            {models.length === 1 && selected && (
              <p className="mt-2 text-xs text-content-muted">Using {selected.name}.</p>
            )}
          </div>

          {busy && <Working seconds={elapsed} />}

          {images.length > 0 && !busy && (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {images.map((image, index) => (
                <Result key={index} image={image} index={index} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The picture being changed, or an invitation to choose one.
 *
 * <p>Shown only for a model that edits. With nothing chosen the page is a
 * plain generator, which is what the same model does when asked for something
 * new -- so this is an addition to the form rather than a mode switch.
 */
function SourcePicture({
  file,
  preview,
  onChange,
  animates,
}: {
  file: File | null
  preview: string | null
  onChange: (file: File | null) => void
  /** A clip starts from the picture rather than changing it. */
  animates: boolean
}) {
  if (file && preview) {
    return (
      <div className="mb-3 flex items-center gap-3 rounded-lg bg-surface-sunken p-2">
        <img
          src={preview}
          alt="The picture being changed"
          className="h-16 w-16 rounded-md object-cover"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{file.name}</p>
          <p className="text-xs text-content-muted">
            {animates ? 'Starting the clip from this picture' : 'Changing this picture'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={animates ? 'Start from nothing instead' : 'Make a new picture instead'}
          className="rounded-lg p-1.5 text-content-muted transition hover:bg-surface hover:text-content"
        >
          <X size={16} />
        </button>
      </div>
    )
  }

  return (
    <label className="mb-3 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-content-muted transition hover:border-accent hover:text-content">
      <Upload size={15} />
      {animates
        ? 'Choose a picture to animate, or leave this empty to start from words'
        : 'Choose a picture to change, or leave this empty to make a new one'}
      <input
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
    </label>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-content-muted">
        {label}
      </span>
      {children}
    </div>
  )
}

/**
 * The wait, stated rather than spun.
 *
 * <p>A minute of a bare spinner is indistinguishable from a hang. Showing the
 * clock and saying up front that this is slow without a graphics card is the
 * difference between waiting and wondering whether to reload.
 */
function Working({ seconds }: { seconds: number }) {
  return (
    <div className="mt-4 flex items-center gap-3 rounded-xl border border-border bg-surface-raised p-4 text-sm">
      <ImageIcon size={18} className="shrink-0 animate-pulse text-accent" />
      <div>
        <p className="font-semibold">Drawing… {formatDuration(seconds)}</p>
        <p className="text-xs text-content-muted">
          Seconds on a graphics card, several minutes without one. You can leave this
          page open and come back.
        </p>
      </div>
    </div>
  )
}

/**
 * What the button offers to do.
 *
 * <p>Four states, written out rather than nested, because nesting them got
 * one wrong: a video model with a picture attached offered to "Change" it,
 * which is what an editing model does. It animates it.
 */
function actionLabel(video: boolean, hasPicture: boolean, busy: boolean): string {
  if (video) {
    if (hasPicture) return busy ? 'Animating…' : 'Animate'
    return busy ? 'Filming…' : 'Film'
  }
  if (hasPicture) return busy ? 'Changing…' : 'Change'
  return busy ? 'Drawing…' : 'Draw'
}

/** Containers that are a clip rather than a picture. */
const VIDEO_FORMATS = ['webm', 'mp4', 'gif']

function Result({ image, index }: { image: GeneratedImage; index: number }) {
  // The engine decides what it writes, and says so. Guessing from the model
  // would be a second place to keep in step with the first.
  const isVideo = VIDEO_FORMATS.includes(image.format)
  const source = `data:${isVideo ? 'video' : 'image'}/${image.format};base64,${image.data}`
  return (
    <figure className="overflow-hidden rounded-xl border border-border bg-surface-raised">
      {isVideo ? (
        // Looping and muted, because these are two seconds long and a clip
        // that plays once has finished before it is looked at.
        <video src={source} controls autoPlay loop muted playsInline className="block w-full" />
      ) : (
        <img src={source} alt={`Generated image ${index + 1}`} className="block w-full" />
      )}
      <figcaption className="flex items-center justify-end border-t border-border px-3 py-2">
        <a
          href={source}
          download={`nevtan-${Date.now()}-${index + 1}.${image.format}`}
          className="flex items-center gap-1.5 text-xs font-semibold text-content-muted transition hover:text-content"
        >
          <Download size={14} />
          Save
        </a>
      </figcaption>
    </figure>
  )
}

/**
 * Nothing to draw with yet.
 *
 * <p>Says which page fixes it rather than only that something is missing. An
 * image model is several gigabytes, so the size is named here instead of
 * being discovered after the download starts.
 */
function NoModels() {
  return (
    <div className="rounded-xl border border-border bg-surface-raised p-6 text-center">
      <ImageIcon size={24} className="mx-auto mb-3 text-content-muted" />
      <p className="mb-1 text-sm font-semibold">No image model installed</p>
      <p className="mx-auto mb-4 max-w-sm text-sm text-content-muted">
        An image model has to be installed before anything can be drawn. They are
        larger than the text ones — several gigabytes — and only need downloading once.
      </p>
      <Link
        to="/models"
        className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-content shadow-raised transition hover:opacity-90"
      >
        Browse models
      </Link>
    </div>
  )
}

/** Minutes once there are minutes, because "212s" is not a duration anyone reads. */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
}
